
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { devLog, devError } from "@/utils/devLog";

export const useLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const hasRedirected = useRef(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [from, setFrom] = useState<string | undefined>(undefined);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear any previous form errors when user types
    if (formError) setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hasRedirected.current) return;
    
    setIsLoading(true);
    setFormError(null);
    
    try {
      devLog("[useLogin] Login attempt", { 
        email: formData.email,
        hasFromState: !!from,
        from
      });
      
      // Only invalidate queries, not clear them completely
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });
      
      if (error) {
        devError("[useLogin] Login error:", error);
        toast.error(error.message || "Failed to log in");
        setFormError(error.message || "Invalid email or password");
        setIsLoading(false);
        return;
      }
      
      devLog("[useLogin] Login successful", { 
        session: !!data.session, 
        user: data.user?.id,
        expiresAt: data.session?.expires_at ? new Date(data.session.expires_at * 1000).toISOString() : null,
        from
      });
      toast.success("Logged in successfully");
      
      // 1) If redirect target present from state, go there
      if (from) {
        devLog("[useLogin] Redirecting to from state path:", from);
        hasRedirected.current = true;
        navigate(from, { replace: true });
        return;
      }
      
      // Check for pending enrollment data
      const pendingEnrollmentData = sessionStorage.getItem('pendingEnrollment');
      
      if (pendingEnrollmentData) {
        try {
          const enrollData = JSON.parse(pendingEnrollmentData);
          devLog("[useLogin] Found pending enrollment:", enrollData);
          
          // If enrollment data has a from path, prioritize it
          if (enrollData.from) {
            devLog("[useLogin] Redirecting to enrollment from path:", enrollData.from);
            hasRedirected.current = true;
            navigate(enrollData.from, { replace: true });
            return;
          }
          
          // Build params for redirect
          const params = new URLSearchParams();
          params.append('course', enrollData.courseId);
          params.append('enroll', 'true');
          if (enrollData.price) params.append('price', enrollData.price);
          if (enrollData.name) params.append('name', enrollData.name);
          
          const redirectUrl = `/student-auth?${params.toString()}`;
          devLog("[useLogin] Redirecting to enrollment:", redirectUrl);
          
          hasRedirected.current = true;
          navigate(redirectUrl, { replace: true });
        } catch (parseError) {
          devError("[useLogin] Error parsing enrollment data:", parseError);
          // Fall back to dashboard redirect
          hasRedirected.current = true;
          navigate('/dashboard', { replace: true });
        }
      } else {
        // No enrollment data, redirect to dashboard
        devLog("[useLogin] No pending enrollment, redirecting to dashboard");
        hasRedirected.current = true;
        navigate('/dashboard', { replace: true });
      }
    } catch (error: any) {
      devError("[useLogin] Unexpected login error:", error);
      toast.error("An unexpected error occurred. Please try again.");
      setFormError("An unexpected error occurred. Please try again.");
      setIsLoading(false);
    }
  };

  return {
    formData,
    formError,
    isLoading,
    handleChange,
    handleSubmit,
    setFrom,
  };
};
